import { z } from 'zod';
import { transformAndFilterValidEdges } from '../../../common/tools/transformEdgesUtils';
import { LOCALES } from '../../../../../utils/Locale';
import { BaseResourceLibraryItemSchema } from './resourceLibraryBaseZodSchemas';
import { AssetBaseSchema } from './assetZodSchemas';

/** Helper to convert e.g. 'zh-cn' -> 'ZhCn', 'en' -> 'En'. */
function toCapitalizedLocaleSuffix(locale: string): string {
    return locale
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('');
}

/** Build up the dynamic shape for "assetLangEn", "assetLangDe", "assetLangZhCn", etc. */
const dynamicLanguageAssetFields = LOCALES.reduce(
    (acc, localeEntry) => {
        const localeCode = localeEntry.locale; // e.g. 'en', 'de', 'zh-cn'
        // Make a property name like "assetLangEn", "assetLangDe", "assetLangZhCn"
        const propertyName = ('assetLang' + toCapitalizedLocaleSuffix(localeCode)) as string;
        acc[propertyName] = AssetBaseSchema.nullable();
        return acc;
    },
    {} as Record<string, z.ZodTypeAny>
);

// ResourceLibraryAssetItem schema for validating the main items
export const ResourceLibraryAssetItemSchema = BaseResourceLibraryItemSchema.extend({
    expirationDate: z.string().nullable(),
    validFromDate: z.string().nullable(),
    lastModificationDate: z.string().nullable(),
})
    .extend(dynamicLanguageAssetFields)
    .describe('ResourceLibraryAssetItemSchema');

// Main schema to validate the entire query response
export const GetResourceLibraryAssetItemListingSchema = z
    .object({
        getResourceLibraryItemListing: z.object({
            edges: z
                .array(
                    z.object({
                        node: z.unknown(),
                    })
                )
                .transform((edges) =>
                    transformAndFilterValidEdges(ResourceLibraryAssetItemSchema, edges)
                ),
        }),
    })
    .describe('GetResourceLibraryAssetItemListingSchema');

// For single item queries
export const GetResourceLibraryAssetItemSchema = z
    .object({
        getResourceLibraryItem: ResourceLibraryAssetItemSchema,
    })
    .describe('GetResourceLibraryAssetItemSchema');
